import React, { useState, useRef, useEffect, useMemo } from 'react';
import { clickEndsPress, isPlainMousePress, usePressAction } from '../hooks/usePressAction'; // [FLUID-8 2026-10-12]
import styled from 'styled-components';
import { fluid } from '../styles/motion'; // [FLUID-1 2026-10-12]
import { fluidPop, fluidCascade } from './Fluid'; // [FLUID-1 2026-10-12]
import { useNavigate, useLocation } from 'react-router-dom';
import { useTheme } from '../contexts/ThemeContext';
import { useAuth } from '../contexts/AuthContext';
import { useIsMobile, MOBILE_MAX_WIDTH } from '../hooks/useIsMobile'; // [MOBILE 2026-07-07]
import { SALES_ROW } from '../config/sections'; // [ROW-BUTTON 2026-09-30]
import { layoutMax } from '../utils/appZoom'; // [APP-FIT 2026-10-26]
// [LIQUID-GLASS 2026-11-02, user] the profile menu (Sign out) uses the shared liquid glass recipe
import {
  lgPopover, lgSquircle, lgHair, lgPress, lgControl, lgControlBg, lgControlHover, lgCardSheen, LG_PRESS, LgPressProps,
} from '../styles/liquidGlass';

/** [APP-FIT 2026-10-26] The outer two clocks hide while the page is laid out 1560 px wide or less (built once, at load). */
const OUTER_CLOCKS_HIDE = layoutMax(1560);

const HeaderContainer = styled.div`
  background: ${(p) => p.theme.surface};
  border-bottom: 1px solid ${(p) => p.theme.line};
  padding: 0 20px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  /* Taller than a bare text bar so the brand mark has room to breathe and the
     top bar reads as a deliberate, branded header rather than a thin strip.
     [HEADER-CAPTIONS 2026-07-15] 58 → 66px on DESKTOP. The captioned actions are
     a ~49px stack (36px control + 3px gap + caption); centred in the old 58px bar
     that left only 4.5px above the control, so the notification badge — which
     floats at top:-7px plus a 2px ring — landed at y≈-3.5px and was cut off by the
     top edge of the VIEWPORT (nothing here sets overflow:hidden; the window itself
     was the clipper). 66px centres the stack at 8.75px, putting the badge body at
     y≈2.75px and its ring at 0.75px — fully visible, with the bar only 8px taller. */
  height: 66px;
  /* Soft elevation so the top bar reads as a defined header floating over the
     content — depth, not a heavy frame. Very low strength, theme-aware.
     position:relative with no z-index paints the shadow over the content below
     without creating a stacking context — so the user-menu dropdown still layers
     above the dock/drawers correctly. */
  box-shadow: 0 2px 10px ${(p) => (p.theme.colorScheme === 'dark' ? 'rgba(0,0,0,0.28)' : 'rgba(22,25,23,0.05)')};
  position: relative;
  transition: background 320ms cubic-bezier(0.4, 0, 0.2, 1), border-color 320ms cubic-bezier(0.4, 0, 0.2, 1), box-shadow 320ms cubic-bezier(0.4, 0, 0.2, 1);

  /* [HEADER-CAPTIONS 2026-07-15] Phones keep the original 58px bar: captions are
     hidden at this same breakpoint, so the control is a lone 34px button centred
     in 58px (badge at y=5px, never clipped) — the geometry the mobile header was
     already tuned for. Must match Caption's breakpoint so the two never drift. */
  @media (max-width: ${MOBILE_MAX_WIDTH}px) {
    height: 58px;
  }

  @media (max-width: 600px) {
    padding: 0 12px;
  }
  /* [LIQUID-GLASS 2026-11-02, user] */
  [data-glow] & { background: ${(p) => (p.theme.colorScheme === 'dark' ? 'rgba(28,28,28,0.72)' : 'rgba(255,255,255,0.72)')}; } /* the page glow shows through softly */
`;

const Left = styled.div`
  display: flex;
  align-items: center;
  gap: 11px;
  min-width: 0;

  @media (max-width: 600px) {
    gap: 8px; /* [PHONE-LAYOUT 2026-10-24] room for the whole brand name (PhoneTitle) */
  }
`;

/* The Scale Agency brand mark — green disc + upward chevron, the two paths
   lifted verbatim from the official tsa-logo.svg (its own 0 0 68 68 viewBox).
   Inlined as SVG (not an <img>) so it stays razor-crisp at any DPI, needs no
   network request, and renders identically on both the white light-mode header
   and the dark-mode slate one (the brand green disc reads on either). The soft
   drop shadow seats it on the bar, matching the header's own elevation. */
const LogoMark = styled.svg`
  width: 32px;
  height: 32px;
  flex-shrink: 0;
  display: block;
  filter: drop-shadow(0 2px 5px ${(p) => (p.theme.colorScheme === 'dark' ? 'rgba(0,0,0,0.45)' : 'rgba(22,25,23,0.18)')});
  transition: filter 320ms cubic-bezier(0.4, 0, 0.2, 1);

  @media (max-width: 600px) {
    width: 28px; /* [PHONE-LAYOUT 2026-10-24] room for the whole brand name (PhoneTitle) */
    height: 28px;
  }
`;

const Title = styled.div`
  font-size: 16px;
  font-weight: 700;
  letter-spacing: -0.2px;
  color: ${(p) => p.theme.text};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;

  /* [MOBILE 2026-07-07] Keep the brand name on phones (per request). Slightly
     smaller + the existing ellipsis so it sits beside the actions and can never
     push them off the edge; the single-button theme toggle (below) reclaims the
     width the old sliding pill used to eat, so the full name fits on most phones. */
  @media (max-width: 600px) {
    font-size: 14px;
  }
`;

/* [PHONE-LAYOUT 2026-10-24, user] Phones only (the isMobile branch of the brand): the WHOLE name, never "…". An
   admin's five round buttons leave the name about 120 px at 360 px ("The Sc…" before), so below 440 px it takes two
   lines — "The Scale Agency" over "Dashboard" — and from 440 px it is one line again. The ellipsis on each line stays
   only as a last resort for a very narrow screen. Desktop keeps Title above, unchanged. */
const PhoneTitle = styled.div`
  display: flex;
  flex-direction: column;
  min-width: 0;
  font-size: 13px;
  font-weight: 700;
  line-height: 1.15;
  letter-spacing: -0.2px;
  color: ${(p) => p.theme.text};

  & > span {
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  @media (min-width: 440px) {
    flex-direction: row;
    gap: 0.28em;
    font-size: 14px;
  }
`;

/* [HEADER-GREETING 2026-08-09] The greeting that sits in the middle of the top bar.
   ABSOLUTELY CENTRED, not a third flex child, and that is the whole point. As a flex item it
   would centre in the space LEFT OVER between the brand and the actions — and since the action
   cluster changes width by role (Bin is admin-only, the bell is admin+senior), the greeting
   would land in a different spot for an admin than for a member. Text that drifts depending on
   who you are reads as a bug. Anchored to the bar's own centre it is identical for everyone.
   The container is already position:relative (deliberately, with no z-index, so the profile
   dropdown keeps layering correctly) — so this needs no structural change to the header.
   pointer-events:none because a centred box spanning the bar must never eat a click aimed at
   the controls behind it. */
/* [US-CLOCKS 2026-08-11] The absolute centring moved UP here when the US clocks arrived, because
   the greeting and its flanking clocks have to centre as ONE object. Everything the comment above
   says still applies, it just applies to the rail now.

   THE INVARIANT: the flanks are two IDENTICAL fixed-width cells a side, so left width == right
   width, so the greeting's centre is still exactly the bar's centre — for an admin, a senior and
   a member alike, whatever the length of the person's name. Give the cells intrinsic width and
   that guarantee is gone: a wider abbreviation on one side would shove the name off-centre. */
const CentreRail = styled.div`
  position: absolute;
  left: 50%;
  top: 50%;
  transform: translate(-50%, -50%);
  pointer-events: none;
  display: flex;
  align-items: center;

  /* [DIALS 2026-08-11] Was a tight centre cluster with ~300px of dead air against the brand and
     the actions — which read as accidental, because the spacing was wildly uneven (36px to the
     name, 300px to the brand). Even distribution is what reads as designed, so the rail now
     claims a fixed width and spreads its three groups across it.

     700px = ~350px reserved each side, which clears the brand (~293px) and the widest action
     cluster (admin, ~252px) with room to breathe — and it is a CONSTANT, so the clocks land in
     the same place for an admin and a member even though their action clusters differ. Capped at
     1040px because past that the internal gaps grow faster than the edge ones and the balance
     inverts. At 1920 this gives roughly 147 / 165 / 165 / 188px across the four gaps.

     gap is the FLOOR, space-between spends whatever is left over — so the layout can never
     collapse into itself on a narrow bar, it just stops spreading. */
  width: min(1040px, calc(100vw / var(--app-zoom, 1) - 700px)); /* [APP-FIT 2026-10-26] the window in page px */
  /* Floor. Without it, a viewport where the calc falls below the content would overflow — and an
     overflowing space-between row spills to the END, which would quietly shove the name off the
     bar's centre and break the one invariant this whole layout exists to hold. max-content rather
     than a hardcoded px so it re-derives itself if a cell width or the gap ever changes. */
  min-width: max-content;
  justify-content: space-between;
  gap: 28px;
  /* One-time settle on load — the bar composes itself instead of snapping into place. */
  animation: header-greet-in 420ms cubic-bezier(0.22, 1, 0.36, 1) both;

  @keyframes header-greet-in {
    from { opacity: 0; transform: translate(-50%, calc(-50% + 4px)); }
    to   { opacity: 1; transform: translate(-50%, -50%); }
  }

  /* Hidden rather than shrunk. The brand and the action cluster already occupy roughly 610px,
     and a greeting squeezed between them — or ellipsised down to "Good even…" — looks worse
     than no greeting at all.
     [US-PREFIX 2026-08-11] 1200 → 1240. Once the cells reached 140px the two-clock rail floors
     at 594px, and at 1201px that put the left flank 11px from the brand — touching. Like the
     $outer breakpoint, this one tracks the cell width; re-check it whenever the cells change.
     [APP-FIT 2026-10-26] Measured on the LAID-OUT width (utils/appZoom), so a scaled 1366 px laptop keeps the greeting. */
  ${layoutMax(1240)} {
    display: none;
  }
`;

/* One side's pair. flex-shrink:0 because these are the width guarantee — a squeezed flank would
   break the symmetry that keeps the name centred. */
const Flank = styled.div`
  display: flex;
  align-items: center;
  gap: 18px;
  flex-shrink: 0;
`;

const Greeting = styled.div`
  /* [HEADER-CLOCK 2026-08-11] A COLUMN: greeting over live clock. The PAIR is what centres in the
     66px bar, so the greeting line rises ~8px from where it sat alone — that is correct, a
     two-line block centres as a block. The two lines share one centre axis rather than a left
     edge, so the stack stays symmetrical whatever the length of the person's name. */
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 3px;
  /* [DIALS 2026-08-11] A fixed cap, not 30vw. This block now feeds the rail's min-width, so a
     viewport-relative cap would let an unusually long name inflate the rail until it collided
     with the brand at the narrow end. 320px comfortably holds the 258px IST line plus a normal
     name; anything longer ellipsises through GreetName, which is what it was built to do. */
  max-width: 320px;
  white-space: nowrap;
`;

/* [HEADER-CLOCK 2026-08-11] The greeting's own row, extracted when the block became a column.
   Carries the type that used to sit on Greeting itself. line-height is pinned to 1.2: a single
   flex row is exactly as tall as its line box, and the browser default (~1.5) would pad 7px of
   dead air between the two lines and make the stack look accidental rather than set. */
const GreetLine = styled.div`
  display: flex;
  align-items: baseline;
  gap: 5px;
  max-width: 100%;
  font-size: 15px;
  line-height: 1.2;
  letter-spacing: -0.1px;
`;

/* [HEADER-CLOCK 2026-08-11] The live line under the name — e.g.
   "Tuesday, 11 August 2026 · 03:42:07 PM IST".

   tabular-nums is not a nicety here, it is the whole reason this doesn't look cheap. The block
   is ABSOLUTELY CENTRED, so any change in text width shifts the entire line sideways — and with
   the proportional figures Plus Jakarta Sans ships by default, `1` is narrower than `8`, so the
   string would change width EVERY SECOND and the greeting above it would visibly shiver. Fixed
   advance widths make the seconds turn over in place. (The 2-digit hour, set in the formatter,
   closes the other width gap: 9:59:59 → 10:00:00.) */
const ClockLine = styled.div`
  display: flex;
  align-items: baseline;
  gap: 6px;
  font-size: 11.5px;
  font-weight: 500;
  line-height: 1.2;
  letter-spacing: 0.2px;
  color: ${(p) => p.theme.text};
  font-variant-numeric: tabular-nums;
  font-feature-settings: 'tnum' 1;
  transition: color 320ms cubic-bezier(0.4, 0, 0.2, 1);
`;

/* Punctuation, not content — dimmed so the eye reads it as a pause between two facts. */
const ClockDot = styled.span`
  opacity: 0.5;
`;

/* The one part that is actually moving gets the extra weight, so the line has an internal
   hierarchy (date → time → zone) instead of reading as one flat grey string. */
const ClockTime = styled.span`
  font-weight: 600;
  letter-spacing: 0.3px;
`;

/* IST is a fixed label and always true: India Standard Time has no daylight saving, so unlike a
   viewer's local abbreviation it never needs deriving. Set smaller and dimmer — it is a unit. */
const ClockZone = styled.span`
  font-size: 10px;
  font-weight: 600;
  letter-spacing: 0.6px;
  /* Raised from 0.72 when the line went white — it now reads as white, with just enough taken off
     to keep it sitting behind the time as a unit rather than competing with it. */
  opacity: 0.85;
`;

/* [US-CLOCKS 2026-08-11] One flanking US clock.
   FIXED width, not intrinsic — this is what holds the name on the bar's centre (see CentreRail).
   86px clears the widest thing it has to hold, "03:24:19 AM", with room to breathe.
   $outer marks the inner pair of the four (Mountain and Central). They drop first when the bar
   narrows: one from each side, so the flanks stay equal and the name does not lurch sideways. */
const ZoneCell = styled.div<{ $outer?: boolean }>`
  /* [DIALS 2026-08-11] A ROW — dial, then the two text lines beside it.
     148px = 32 (dial) + 9 (gap) + 107 for the text. The LABEL now drives it, not the time:
     "US · MDT · Wed" at 10.5px/600 needs ~97px (M and W are the wide letters) against ~89px for
     "03:45:01 AM" at 13px/600 — so ~10px of headroom on the binding one.

     Sized 104 → 116 → 130 → 140 → 148 across five rounds, and the lesson is in that sequence:
     104px left 70px against the ~71px the time needed and the "AM" silently dropped to a second
     line. Estimates from font metrics have run light every time, so carry real headroom rather
     than fitting to the calculation. Still FIXED width, for the centring reason above, with
     nowrap below — which means an undersized cell OVERFLOWS visibly rather than reflowing, and
     the width must be re-derived whenever the type or the label text changes. */
  width: 148px;
  flex-shrink: 0;
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 9px;
  white-space: nowrap;

  /* [LIQUID-GLASS 2026-11-02, user] "also for the above clocks": each clock sits in a glass capsule (light top edge,
     soft lower reflection, thin hairline). Drawn by ::before BEHIND the cell and 6 px past its left / top / bottom, so the
     cell keeps its fixed 148 px (the centring above) — the gaps between clocks never drop under 28 px, so the capsules
     never touch. No blur: the bar behind is a flat colour, so translucency alone reads as glass, at no cost. */
  position: relative;
  isolation: isolate;
  &::before {
    content: '';
    position: absolute;
    inset: -6px 2px -6px -6px;
    border-radius: 999px;
    background-color: ${lgControlBg};
    background-image: ${lgCardSheen};
    box-shadow:
      inset 0 1px 0 ${(p) => (p.theme.colorScheme === 'dark' ? 'rgba(255,255,255,0.22)' : 'rgba(255,255,255,0.95)')},
      inset 0 -1px 0 ${(p) => (p.theme.colorScheme === 'dark' ? 'rgba(255,255,255,0.08)' : 'rgba(255,255,255,0.6)')},
      0 0 0 1px ${lgHair},
      ${(p) => (p.theme.colorScheme === 'dark' ? '0 6px 16px -10px rgba(0,0,0,0.6)' : '0 6px 16px -10px rgba(22,25,23,0.22)')};
    z-index: -1;
    pointer-events: none;
  }

  /* This breakpoint TRACKS THE CELL WIDTH — 1400 → 1450 → 1500 → 1530 → 1560 as the cells grew.
     At 148px the rail's floor is 942px, and below ~1560 that floor runs the left flank into the
     brand. Re-verify the whole 1241→2560px range after any size change.
     [APP-FIT 2026-10-26] Measured on the LAID-OUT width (utils/appZoom): a scaled 1366 px laptop is laid out 1920 px wide
     and shows all four clocks, as the 1920 screen does. */
  ${(p) => p.$outer && `
  ${OUTER_CLOCKS_HIDE} {
    display: none;
  }`}
`;

/* [DIALS 2026-08-11] The face. Hour and minute hands only — a second hand would be a 10px sliver
   sweeping on four faces at once, and the digits beside it already carry the seconds to the exact
   second, so it would be noise paying for nothing.

   THE RING IS THE STATUS LIGHT. It replaces the separate 5px dot: green + a soft glow during
   business hours (09:00–17:00 Mon–Fri in that city), muted otherwise. One element carrying one
   meaning beats a dot and a dial competing beside each other. Everything the old dot's comment
   argued still holds — in particular the cell is never dimmed as a whole, because at 3:54 PM IST
   every US clock is 3–6 AM and all four would grey out through most of the Indian workday. */
const Dial = styled.svg<{ $open: boolean }>`
  width: 32px;
  height: 32px;
  flex-shrink: 0;
  overflow: visible;

  .dial-ring {
    /* [LIQUID-GLASS 2026-11-02] a light glass fill inside the ring (was empty) */
    fill: ${(p) => (p.theme.colorScheme === 'dark' ? 'rgba(255,255,255,0.06)' : 'rgba(255,255,255,0.75)')};
    stroke: ${(p) => (p.$open ? p.theme.green600 : p.theme.muted)};
    opacity: ${(p) => (p.$open ? 1 : 0.4)};
    filter: ${(p) => (p.$open ? `drop-shadow(0 0 3px ${p.theme.green600}88)` : 'none')};
    transition: stroke 320ms cubic-bezier(0.4, 0, 0.2, 1), opacity 320ms cubic-bezier(0.4, 0, 0.2, 1), filter 320ms cubic-bezier(0.4, 0, 0.2, 1);
  }
  /* No transition on the hands: at 6° a minute you would never see the tween, and it would spin
     backwards through 354° on every :59 → :00 rollover. */
  .dial-hand {
    stroke: ${(p) => p.theme.text};
    stroke-linecap: round;
    transition: stroke 320ms cubic-bezier(0.4, 0, 0.2, 1);
  }
  .dial-pin {
    fill: ${(p) => p.theme.text};
    transition: fill 320ms cubic-bezier(0.4, 0, 0.2, 1);
  }
`;

/* The two text rows, LEFT-aligned now that a dial sits to their left — centring them would leave
   the label and the time on two different left edges beside a fixed circle. */
const ZoneText = styled.div`
  display: flex;
  flex-direction: column;
  gap: 3px;
  min-width: 0;
`;

/* 9.5px/600 is the SAME spec as the Bin / Notification / Theme captions across the bar, so the
   zone labels read as part of the header's existing type system rather than a new voice. */
const ZoneLabel = styled.div`
  font-size: 10.5px;
  font-weight: 600;
  line-height: 1;
  letter-spacing: 0.4px;
  /* [DIALS 2026-08-11] theme.text, NOT a hardcoded #fff — it resolves to #f8fafc on the dark bar
     (the white that was asked for) and to #000000 in the light theme, where a literal white would
     be invisible against the surface. The clocks lead on colour now; the name still leads overall
     on size and centre position. */
  color: ${(p) => p.theme.text};
  transition: color 320ms cubic-bezier(0.4, 0, 0.2, 1);
`;

/* Identical type to ClockTime on the IST line — one spec for "a clock" everywhere in the bar, so
   the five read as one instrument. The centre wins on position and on the name above it, not by
   shouting. tabular-nums for the same reason as the IST line, and it matters more here: four more
   counters, all turning over at once. */
const ZoneTime = styled.div`
  font-size: 13px;
  font-weight: 600;
  line-height: 1.2;
  letter-spacing: 0.3px;
  color: ${(p) => p.theme.text};
  font-variant-numeric: tabular-nums;
  font-feature-settings: 'tnum' 1;
  transition: color 320ms cubic-bezier(0.4, 0, 0.2, 1);
`;

/* Two weights on one line is what makes this read as typography rather than a label: the
   greeting recedes, the name carries. Tokens only, so it moves with the theme. */
const GreetWord = styled.span`
  /* [DIALS 2026-08-11] Was theme.muted. Now white like the rest of the bar — the greeting still
     sits behind the name, but on WEIGHT (500 vs 600) rather than on colour. */
  color: ${(p) => p.theme.text};
  font-weight: 500;
  transition: color 320ms cubic-bezier(0.4, 0, 0.2, 1);
`;

const GreetName = styled.span`
  color: ${(p) => p.theme.text};
  font-weight: 600;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  transition: color 320ms cubic-bezier(0.4, 0, 0.2, 1);
`;

/* [PM-LOGIN 2026-08-11] A username/password session has no top bar at all — the whole
   HeaderContainer is skipped and this is rendered instead: the profile circle alone, floating
   over the page.

   position:fixed so it occupies NO layout space, which is what lets the content below reclaim
   the 66px the bar used to take. The Header is in-flow inside AppLayout's frame, so anything
   in-flow here (even an empty bar) would keep that gap. z-index sits under the profile menu's own
   1300 so the dropdown still paints above it, and above ordinary page content so the circle is
   never buried by a sticky table header. */
const FloatingProfile = styled.div`
  position: fixed;
  top: 14px;
  right: 20px;
  z-index: 1200;

  /* [PM-SG 2026-08-11] Held at 25% at rest. This is the only chrome on a full-screen board
     that runs on a wall for hours, and at full strength it read as a notification sitting over
     the race. It comes back to full on hover or keyboard focus, so the one thing it does —
     signing out — is never harder to reach for somebody actually at the machine. */
  opacity: 0.25;
  transition: opacity 240ms cubic-bezier(0.4, 0, 0.2, 1);

  &:hover,
  &:focus-within {
    opacity: 1;
  }

  @media (max-width: 600px) {
    top: 10px;
    right: 12px;
  }
`;

const Actions = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex-shrink: 0;

  /* [MOBILE 2026-07-07] Tighter gap on phones so the brand title beside the logo
     keeps as much room as possible.
     [PHONE-LAYOUT 2026-10-24] 6 → 5 px, with the 32 px buttons (IconButton / Avatar), for the whole name. */
  @media (max-width: 600px) {
    gap: 5px;
  }
`;

/* [HEADER-CAPTIONS 2026-07-15] Each header action is its control stacked over a
   small caption, so the glyphs read as named affordances instead of icons you
   have to hover to identify. DESKTOP ONLY — see Caption's media query. */
const ActionItem = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 3px;
`;

/* Fixed at 36px — the tallest control (the theme switch; the round buttons and
   avatar are 34px). Pinning the slot height is what puts every caption on the
   SAME baseline instead of 1px apart. */
const ActionSlot = styled.div`
  height: 36px;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const Caption = styled.span`
  font-size: 9.5px;
  font-weight: 600;
  letter-spacing: 0.2px;
  line-height: 1;
  white-space: nowrap;
  color: ${(p) => p.theme.muted};
  /* Pure label: never swallow a click or block the control's hover tooltip. */
  pointer-events: none;
  user-select: none;

  @media (max-width: ${MOBILE_MAX_WIDTH}px) {
    display: none;
  }
`;

/* [LIQUID-GLASS 2026-11-02] Light theme only: the white glass of lgControl is invisible on the white bar (checked on a
   screenshot), so the bar's round buttons and the theme switch's track get a soft grey tint and a thin edge there. */
const HEADER_GLASS_LIGHT = `
  background-color: rgba(22, 25, 23, 0.05);
  box-shadow: 0 0 0 1px rgba(22, 25, 23, 0.11), 0 1px 2px rgba(22, 25, 23, 0.06), 0 6px 14px -8px rgba(22, 25, 23, 0.25);
  &:hover:not(:disabled) { background-color: rgba(22, 25, 23, 0.09); }
`;

/* [LIQUID-GLASS 2026-11-02, user] The top bar's round buttons (App Alerts, Bin, Notification) are the round glass of the
   panels' close button (styles/liquidGlass.ts): see-through, a light rim brighter on the top edge, a little brighter under
   the mouse, and a squish + soft light where pressed. No blur: the bar behind is a flat colour. */
const IconButton = styled.div.attrs<LgPressProps>(LG_PRESS)`
  position: relative;
  width: 34px;
  height: 34px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  font-size: 13px;
  isolation: isolate;
  overflow: hidden;
  color: ${(p) => p.theme.colorScheme === 'dark' ? 'rgba(255,255,255,0.85)' : p.theme.text};
  ${lgControl}
  ${lgPress}
  /* Light theme: white glass vanishes on the white bar, so the circle takes a soft grey tint and a thin edge. */
  ${(p) => (p.theme.colorScheme === 'dark' ? '' : HEADER_GLASS_LIGHT)}

  & > * { position: relative; z-index: 1; }

  @media (max-width: 600px) {
    width: 32px; /* [PHONE-LAYOUT 2026-10-24] room for the whole brand name (PhoneTitle) */
    height: 32px;
  }
`;

const Avatar = styled.div`
  position: relative;
  width: 34px;
  height: 34px;

  @media (max-width: 600px) {
    width: 32px; /* [PHONE-LAYOUT 2026-10-24] as the round buttons */
    height: 32px;
  }
  border-radius: 50%;
  display: grid;
  place-items: center;
  font-weight: 700;
  font-size: 13px;
  color: #fff;
  letter-spacing: 0.2px;
  isolation: isolate;
  overflow: hidden;
  cursor: pointer;
  /* [LIQUID-GLASS 2026-11-02, user] The glossy green of the glass Save / Add buttons (shine over the top half, light top
     edge, soft glow); no blur needed on the flat bar. */
  border: none;
  background: linear-gradient(180deg, rgba(255,255,255,0.34), rgba(255,255,255,0) 52%), ${(p) => p.theme.colorScheme === 'dark'
    ? 'linear-gradient(180deg, #3ccf8f 0%, #1f9e66 55%, #178a57 100%)'
    : 'linear-gradient(180deg, #22c495 0%, #12936b 55%, #0d7f5d 100%)'};
  box-shadow:
    0 6px 16px -6px ${(p) => p.theme.colorScheme === 'dark' ? 'rgba(74,205,139,0.55)' : 'rgba(16,185,129,0.50)'},
    inset 0 1px 0 rgba(255,255,255,0.55),
    inset 0 -1px 0 rgba(0,0,0,0.16),
    inset 0 0 0 1px rgba(255,255,255,0.14);
  text-shadow: 0 1px 2px rgba(0,0,0,0.25);
  transition: transform 120ms cubic-bezier(0.4, 0, 0.2, 1), filter 120ms cubic-bezier(0.4, 0, 0.2, 1), box-shadow 180ms cubic-bezier(0.4, 0, 0.2, 1);

  & > * { position: relative; z-index: 1; }

  &:hover {
    filter: brightness(1.06);
  }

  &:active { transform: translateY(1px); }
`;

const NotifBadge = styled.span`
  position: absolute;
  top: -7px;
  right: -7px;
  /* [LIQUID-GLASS 2026-11-02] glossy: a soft shine over the top half and a light top edge */
  background: linear-gradient(180deg, rgba(255,255,255,0.35), rgba(255,255,255,0) 60%), linear-gradient(180deg, rgba(239,68,68,1) 0%, rgba(220,38,38,1) 100%);
  color: #fff;
  font-size: 9px;
  font-weight: 700;
  min-width: 15px;
  height: 15px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0 4px;
  line-height: 1;
  box-shadow:
    0 2px 6px rgba(220, 38, 38, 0.55),
    inset 0 1px 0 rgba(255, 255, 255, 0.45),
    0 0 0 2px ${(p) => p.theme.surface};

  /* [PHONE-LAYOUT 2026-10-22] Phones: the count at 11px (9px was below the phone's 10px floor). Same corner. */
  @media (max-width: 768px) {
    font-size: 11px;
    min-width: 17px;
    height: 17px;
    border-radius: 9px;
  }
`;

const BellWrapper = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const ThemeSwitch = styled.button<{ $isDark: boolean }>`
  position: relative;
  display: inline-grid;
  grid-template-columns: 1fr 1fr;
  align-items: center;
  width: 76px;
  height: 36px;
  padding: 0;
  border-radius: 999px;
  cursor: pointer;
  outline: none;
  overflow: hidden;
  isolation: isolate;
  /* [LIQUID-GLASS 2026-11-02, user] The track is the round buttons' glass (light rim, a little brighter under the mouse);
     the sun / moon knob was already glossy. */
  border: none;
  ${lgControl}
  ${(p) => (p.theme.colorScheme === 'dark' ? '' : HEADER_GLASS_LIGHT)}
  transition: background 180ms cubic-bezier(0.4, 0, 0.2, 1), box-shadow 180ms cubic-bezier(0.4, 0, 0.2, 1);

  &:focus-visible {
    box-shadow:
      0 0 0 3px ${(p) => p.theme.green600}40,
      0 4px 12px ${(p) => p.theme.colorScheme === 'dark' ? 'rgba(0,0,0,0.35)' : 'rgba(0,0,0,0.10)'};
  }
`;

const SwitchKnob = styled.span.attrs<{ $isDark: boolean }>({ 'data-theme-knob': '' } as any)<{ $isDark: boolean }>`
  position: absolute;
  top: 4px;
  bottom: 4px;
  left: 4px;
  /* [FLUID-8 2026-10-12] Slides with transform (was left): it runs on the compositor, so it keeps moving while the
     page re-colours for the new theme (a whole-app re-render). calc(100% + 2px) = the old left travel exactly. */
  transform: ${(p) => (p.$isDark ? 'translateX(calc(100% + 2px))' : 'none')};
  width: calc(50% - 5px);
  border-radius: 999px;
  background: ${(p) => p.$isDark
    ? 'linear-gradient(180deg, rgba(96,165,250,0.92) 0%, rgba(59,130,246,1) 100%)'
    : 'linear-gradient(180deg, rgba(253,224,71,0.95) 0%, rgba(245,158,11,1) 100%)'};
  box-shadow:
    0 4px 10px ${(p) => p.$isDark ? 'rgba(59,130,246,0.45)' : 'rgba(245,158,11,0.45)'},
    inset 0 1px 0 rgba(255,255,255,0.55),
    inset 0 -1px 0 rgba(0,0,0,0.12);
  transition: transform 0.42s cubic-bezier(0.32, 0.72, 0, 1), background 0.28s ease, box-shadow 0.28s ease;
  z-index: 1;

  &::before {
    content: '';
    position: absolute;
    inset: 0;
    border-radius: 999px;
    background: linear-gradient(180deg, rgba(255,255,255,0.45) 0%, rgba(255,255,255,0) 55%);
    pointer-events: none;
  }
`;

const ThemeIcon = styled.i<{ $active: boolean; $kind: 'sun' | 'moon' }>`
  position: relative;
  z-index: 2;
  font-size: 13px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: color 180ms cubic-bezier(0.4, 0, 0.2, 1), transform 180ms cubic-bezier(0.4, 0, 0.2, 1);
  color: ${(p) =>
    p.$active
      ? '#ffffff'
      : (p.theme.colorScheme === 'dark' ? 'rgba(255,255,255,0.55)' : 'rgba(22,25,23,0.45)')};
  text-shadow: ${(p) => (p.$active ? '0 1px 2px rgba(0,0,0,0.25)' : 'none')};
  transform: ${(p) => (p.$active ? 'scale(1.0)' : 'scale(0.92)')};
`;

const AvatarImg = styled.img`
  width: 34px;
  height: 34px;

  @media (max-width: 600px) {
    width: 32px; /* [PHONE-LAYOUT 2026-10-24] as the round buttons */
    height: 32px;
  }
  border-radius: 50%;
  object-fit: cover;
  cursor: pointer;
  border: 1px solid ${(p) => p.theme.colorScheme === 'dark' ? 'rgba(70, 191, 130, 0.55)' : 'rgba(26, 159, 125, 0.55)'};
  box-shadow: 0 4px 12px ${(p) => p.theme.colorScheme === 'dark' ? 'rgba(0,0,0,0.4)' : 'rgba(0,0,0,0.15)'};
  transition: transform 120ms cubic-bezier(0.4, 0, 0.2, 1), filter 120ms cubic-bezier(0.4, 0, 0.2, 1);
  &:hover { filter: brightness(1.05); }
  &:active { transform: translateY(1px); }
`;

/* [HEADER-CAPTIONS 2026-07-15] Column so the "Profile" caption stacks under the
   avatar, matching the other actions. Stays the menu's positioning context and
   the outside-click ref target; Menu is absolute so it's out of this flow and
   simply opens 10px below the caption now. */
const UserWrap = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 3px;
`;

/* [LIQUID-GLASS 2026-11-02, user] "when i click on the profile … where we have sign out … it should also be in the liquid
   thing": real frosted glass with the light top edge and smooth 18 px corners (styles/liquidGlass.ts). Was a solid
   raised card. Nothing above it blurs or fades, so the blur reaches the page behind. */
const Menu = styled.div<{ $open: boolean }>`
  position: absolute;
  top: calc(100% + 10px);
  right: 0;
  min-width: 248px;
  padding: 6px;
  ${lgPopover /* [STALE-PRESS 2026-11-02, user] glass without the live blur: it opens next to the top bar's buttons */}
  ${lgSquircle(18)}
  z-index: 1300;
  /* [FLUID-1 2026-10-12, user] Grows out of the avatar's corner on the spring, its rows rising in one
     after another (components/Fluid.tsx); hidden for real once the 200 ms close has played. Replaces
     the old 6px / 180 ms fade. */
  ${(p) => fluidPop({
    open: p.$open,
    origin: '100% 0',
    from: 'translateY(-10px) scale(0.9)',
    extra: `visibility 0s linear ${p.$open ? '0s' : fluid.out}`,
  })}
  visibility: ${(p) => (p.$open ? 'visible' : 'hidden')};
  ${(p) => (p.$open ? fluidCascade('> *', 0, 12) /* [FLUID-8 2026-10-12] soft: readable at once */ : '')}

  /* [PHONE-LAYOUT 2026-10-22] Phones: a little wider (never past the screen), so a long name or email has room;
     they wrap rather than end in "..." (see MenuName / MenuEmail). */
  @media (max-width: 768px) {
    min-width: 0;
    width: min(300px, calc(100vw - 24px));
  }
`;

const MenuHead = styled.div`
  display: flex;
  align-items: center;
  gap: 11px;
  padding: 12px 12px 14px;
`;

const MenuIdentity = styled.div`
  min-width: 0;
  text-align: left;
`;

const MenuName = styled.div`
  font-size: 14px;
  font-weight: 700;
  color: ${(p) => p.theme.text};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;

  /* [PHONE-LAYOUT 2026-10-22] Phones: wrap, never cut (no hover there to read the rest). */
  @media (max-width: 768px) {
    white-space: normal;
    overflow-wrap: anywhere;
  }
`;

const MenuEmail = styled.div`
  font-size: 12px;
  color: ${(p) => p.theme.muted};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;

  /* [PHONE-LAYOUT 2026-10-22] Phones: wrap, never cut. */
  @media (max-width: 768px) {
    white-space: normal;
    overflow-wrap: anywhere;
  }
`;

const RoleBadge = styled.span<{ $role: string }>`
  display: inline-block;
  margin-top: 5px;
  padding: 2px 9px;
  border-radius: 999px;
  font-size: 10.5px;
  font-weight: 700;
  letter-spacing: 0.3px;
  text-transform: uppercase;
  color: #fff;
  background: ${(p) =>
    p.$role === 'admin'
      ? 'linear-gradient(180deg, #6366f1, #4f46e5)'
      : p.$role === 'lead'
      ? 'linear-gradient(180deg, #0ea5e9, #0284c7)'
      : 'linear-gradient(180deg, ' + p.theme.green600 + ', ' + p.theme.green700 + ')'}; /* [GRAPHITE-EMERALD] jade light / emerald dark */
  box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.35); /* [LIQUID-GLASS 2026-11-02] a glossy top edge */

  /* [PHONE-LAYOUT 2026-10-22] Phones: 11px, the phone label floor. */
  @media (max-width: 768px) {
    font-size: 11px;
  }
`;

const MenuDivider = styled.div`
  height: 1px;
  background: ${lgHair};
  margin: 2px 6px;
`;

/* [LIQUID-GLASS 2026-11-02] Rounder rows (12 px); a press squishes the row a little and glows where it was pressed. */
const LogoutItem = styled.button.attrs<LgPressProps>(LG_PRESS)`
  position: relative;
  display: flex;
  align-items: center;
  gap: 10px;
  width: 100%;
  padding: 10px 12px;
  border: none;
  border-radius: 12px;
  background: transparent;
  color: ${(p) => p.theme.danger};
  font-size: 13.5px;
  font-weight: 600;
  cursor: pointer;
  ${lgPress}
  text-align: left;

  &:hover { background: ${(p) => (p.theme.colorScheme === 'dark' ? 'rgba(220,38,38,0.14)' : 'rgba(220,38,38,0.08)')}; }

  /* [PHONE-LAYOUT 2026-10-22] Phones: a 44px tall row to tap. */
  @media (max-width: 768px) {
    min-height: 44px;
  }
`;

/* [PHONE-LAYOUT 2026-10-24, user] Phones only: the theme switch is a row of the profile menu ("Light mode" /
   "Dark mode") — its round button left the top bar so the whole brand name fits. Same row as Sign out, in ink. */
const ThemeItem = styled(LogoutItem)`
  color: ${(p) => p.theme.text};

  &:hover { background: ${lgControlHover}; } /* [LIQUID-GLASS 2026-11-02] a soft glass highlight */

  i {
    width: 16px;
    text-align: center;
    color: ${(p) => p.theme.muted};
  }
`;

const ROLE_LABEL: Record<string, string> = {
  admin: 'Admin',
  lead: 'Team Lead',
  member: 'Member',
};

interface HeaderProps {
  onNotifToggle?: () => void;
  notifCount?: number;
  onTrashToggle?: () => void;
  trashCount?: number;
  // [APP-ALERTS 2026-09-04] Faults the app found in itself — admin-only, sits left of the Bin.
  onAlertsToggle?: () => void;
  alertsCount?: number;
}

/* [HEADER-GREETING 2026-08-09] Greetings that are only ever offered when they are TRUE.
   Time-bound lines are gated to their own window (read from the viewer's own clock, not the
   app's PST/IST reporting calendar — this is about the person reading the screen, not the
   reporting month), and the neutral lines are valid at any hour. The pick is drawn from
   {this hour's lines} ∪ {neutral}, so it varies without ever being able to say "Good morning"
   at nine at night. */
const NEUTRAL_GREETINGS = ['Welcome back', 'Good to see you', 'Hello again'];

function greetingsForHour(hour: number): string[] {
  if (hour >= 5 && hour < 12) return ['Good morning', 'Morning'];
  if (hour >= 12 && hour < 17) return ['Good afternoon', 'Afternoon'];
  if (hour >= 17 && hour < 22) return ['Good evening', 'Evening'];
  return ['Working late']; // 22:00–04:59 — no "good morning/evening" would be honest here
}

/* The full name on the account. Falls back to reconstructing it from the address they signed in
   with (manas.mehrotra@… → "Manas Mehrotra") because `name` is nullable on MeResponse. It never
   falls back to the raw email: "Good evening, manas.mehrotra@rnf.tech" would undo the whole
   effect. No name and no address at all → null, and the greeting is shown on its own. */
function fullNameFrom(name?: string | null, email?: string | null): string | null {
  const n = (name || '').trim();
  if (n) return n;
  const local = (email || '').trim().split('@')[0];
  const parts = local.split(/[._\-+]+/).filter(Boolean);
  if (!parts.length) return null;
  return parts
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
    .join(' ');
}

/* [HEADER-CLOCK 2026-08-11] ────────────────────────────────────────────────────────────────
   The clock is pinned to Asia/Kolkata, NOT to the machine it renders on.

   The line ends in the word "IST", so it has to actually be IST. Formatting the browser's own
   clock and labelling it IST would be wrong for anyone outside India — and wrong twice, because
   at 8pm Pacific the date in India is already TOMORROW, so the weekday would be a day out too.
   IST is also this product's own working clock (the effective-month rollover is 12:30 IST), so
   a fixed India clock in the header is a fact people can act on rather than decoration.

   One formatter, one call, one instant: the weekday, the date and the time are pulled from the
   SAME formatToParts() result, so they can never disagree with each other across midnight.
   Assembled from the parts rather than taking the locale's finished string, so the order, the
   separator and the case of AM/PM are ours — an ICU update can reword "at" or lower-case the
   day period without touching what the header shows. */
const IST_TIME_ZONE = 'Asia/Kolkata';

// [DATE-STYLE 2026-09-29, user] One date style across the app: the date reads "Tuesday, Sep 29, 2026"
// (was "Tuesday, 29 September 2026"). 'en-US' parts, because en-GB spells the short month "Sept".
const IST_FORMAT: Intl.DateTimeFormat = (() => {
  const opts: Intl.DateTimeFormatOptions = {
    weekday: 'long',
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: '2-digit', // padded, so the line never changes width at 9:59:59 → 10:00:00
    minute: '2-digit',
    second: '2-digit',
    hour12: true,
  };
  try {
    return new Intl.DateTimeFormat('en-US', { ...opts, timeZone: IST_TIME_ZONE });
  } catch {
    // A runtime without the IANA zone would otherwise throw on every tick. Degrading to the
    // local clock keeps the header alive; the zone is present in every browser this app targets.
    return new Intl.DateTimeFormat('en-US', opts);
  }
})();

function istNowParts(at: Date): { date: string; time: string } {
  const p: Record<string, string> = {};
  for (const part of IST_FORMAT.formatToParts(at)) {
    if (part.type !== 'literal') p[part.type] = part.value;
  }
  return {
    date: `${p.weekday}, ${p.month} ${p.day}, ${p.year}`, // [DATE-STYLE 2026-09-29]
    // Some ICU builds spell the day period "a.m."; normalised so it is always AM / PM.
    time: `${p.hour}:${p.minute}:${p.second} ${(p.dayPeriod || '').replace(/\./g, '').toUpperCase()}`,
  };
}

/* [US-CLOCKS 2026-08-11] ────────────────────────────────────────────────────────────────────
   The four flanking clocks: the contiguous US zones, in map order west → east across the bar, so
   time also increases left to right. Chosen on the data, not by feel — against 19,884 US-mapped
   rows in SEO_Hubspot_Raw.Leads_Raw and 58,369 in Companies_Raw.time_zone (two pipelines, and
   they agree within 0.6 of a point) these four are 99.2% / 99.5% of everything:

     Eastern 48.5% · Central 22.5% · Pacific 21.5% · Mountain 6.7% · Hawaii 0.5% · Alaska 0.3%

   Which is also why Mountain keeps its slot over Hawaii or Alaska — it is eight times the two of
   them combined. The same four cover ~3,400 Canadian companies (Toronto, Regina, Vancouver,
   Edmonton, Montreal) for free.

   KNOWN AND UNFIXABLE WITHOUT A FIFTH CLOCK: America/Phoenix (1,325 companies) is MST all year —
   Arizona does not observe DST — so for the eight daylight-saving months Arizona is really on
   Pacific time and this Mountain clock reads an hour ahead for those clients.

   The ABBREVIATION IS DERIVED, never hardcoded. It is PDT/MDT/CDT/EDT today and PST/MST/CST/EST
   from November; writing "EST" into the source would be wrong for eight months of every year and
   an hour out from what the reader expects. (India is the mirror image: Intl returns "GMT+5:30"
   rather than "IST" for Asia/Kolkata, and IST has no daylight saving — which is why that one is
   a literal and these four are not.) */
interface UsZone {
  key: string;
  timeZone: string;
}

const US_ZONES_LEFT: UsZone[] = [
  { key: 'pacific', timeZone: 'America/Los_Angeles' },
  { key: 'mountain', timeZone: 'America/Denver' },
];

const US_ZONES_RIGHT: UsZone[] = [
  { key: 'central', timeZone: 'America/Chicago' },
  { key: 'eastern', timeZone: 'America/New_York' },
];

/* Built once per zone at module load, not per tick — constructing an Intl.DateTimeFormat is the
   expensive part of formatting, and this one would otherwise be rebuilt 345,600 times a day. */
const US_FORMATS: Record<string, Intl.DateTimeFormat> = {};
for (const z of [...US_ZONES_LEFT, ...US_ZONES_RIGHT]) {
  const opts: Intl.DateTimeFormatOptions = {
    weekday: 'short',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: true,
    timeZoneName: 'short', // ← the DST-aware abbreviation
  };
  try {
    US_FORMATS[z.key] = new Intl.DateTimeFormat('en-US', { ...opts, timeZone: z.timeZone });
  } catch {
    US_FORMATS[z.key] = new Intl.DateTimeFormat('en-US', opts);
  }
}

interface UsZoneReading {
  abbr: string;
  weekday: string;
  time: string;
  open: boolean;
  hourAngle: number;
  minuteAngle: number;
}

function usZoneReading(at: Date, key: string): UsZoneReading {
  const p: Record<string, string> = {};
  for (const part of US_FORMATS[key].formatToParts(at)) {
    if (part.type !== 'literal') p[part.type] = part.value;
  }
  const period = (p.dayPeriod || '').replace(/\./g, '').toUpperCase();

  /* Back to a 24-hour value for the business-hours test. Derived from the 12-hour parts rather
     than asking Intl a second time: `% 12` folds 12 to 0, so 12 AM → 0 and 12 PM → 12. */
  const hour24 = (Number(p.hour) % 12) + (period === 'PM' ? 12 : 0);

  const h12 = Number(p.hour) % 12; // 12 o'clock is straight up, i.e. angle 0
  const min = Number(p.minute);

  return {
    abbr: p.timeZoneName || '',
    weekday: p.weekday || '',
    time: `${p.hour}:${p.minute}:${p.second} ${period}`,
    // Weekend excluded on purpose — a lit "call them now" light on a Saturday is worse than none.
    open: hour24 >= 9 && hour24 < 17 && p.weekday !== 'Sat' && p.weekday !== 'Sun',
    // The hour hand carries the minutes too, or it would sit on the hour mark until it jumped.
    hourAngle: (h12 + min / 60) * 30,
    minuteAngle: min * 6,
  };
}

const ZoneClock: React.FC<{ now: Date; zone: UsZone; outer?: boolean }> = ({ now, zone, outer }) => {
  const r = usZoneReading(now, zone.key);
  return (
    <ZoneCell $outer={outer}>
      {/* Decorative: the time it shows is spelled out exactly, to the second, immediately to its
          right — so there is nothing here for a screen reader to gain. */}
      <Dial $open={r.open} viewBox="0 0 100 100" aria-hidden="true" focusable="false">
        <circle className="dial-ring" cx="50" cy="50" r="45" fill="none" strokeWidth="6" />
        <line
          className="dial-hand" x1="50" y1="50" x2="50" y2="28" strokeWidth="9"
          transform={`rotate(${r.hourAngle} 50 50)`}
        />
        <line
          className="dial-hand" x1="50" y1="50" x2="50" y2="17" strokeWidth="6"
          transform={`rotate(${r.minuteAngle} 50 50)`}
        />
        <circle className="dial-pin" cx="50" cy="50" r="5" />
      </Dial>
      <ZoneText>
        {/* The weekday is not decoration. India runs 9.5–13.5 hours ahead, so through the whole
            Indian morning the US is still on YESTERDAY — at 9:00 AM Tuesday here it is 11:30 PM
            Monday in New York. A US clock without a day marker is actively misleading. */}
        {/* [US-PREFIX 2026-08-11] "US" on every cell, at full strength like the rest of the
            label. On each one rather than once over the group, because the two flanks sit at
            opposite ends of the bar with a name between them — a single shared heading would
            not read as covering both. */}
        <ZoneLabel>US · {r.abbr} · {r.weekday}</ZoneLabel>
        <ZoneTime>{r.time}</ZoneTime>
      </ZoneText>
    </ZoneCell>
  );
};

/* Its OWN component, deliberately — this is the difference between a clock and a performance
   problem. A per-second setState inside Header would re-render the brand SVG, the title, every
   action item, the theme switch, the avatar and the whole profile menu, and make
   styled-components re-run each of those gradient/shadow interpolations — 86,400 times a day.
   Isolated here, the tick re-renders the centre rail and nothing else.

   [US-CLOCKS 2026-08-11] It owns the tick for ALL FIVE clocks, and that is now load-bearing
   rather than tidy. With minutes only, five independent timers drifting apart would have been
   invisible; with seconds on every face it is the first thing you would notice — five counters
   flipping a few hundred milliseconds apart reads as five broken widgets. Driven from one piece
   of state they turn over on the same frame, which is what makes the bar read as one instrument.
   (Expect every clock to show the IDENTICAL seconds digit: every US zone is a whole-hour offset
   from UTC, so only India's +5:30 shifts the minutes. That is correct, not a sync artefact.) */
const HeaderCentre: React.FC<{ greetWord: string; greetName: string | null }> = ({ greetWord, greetName }) => {
  // Seeded synchronously so the very first paint is already the right time: no blank slot and
  // no "--:--:--" placeholder flashing for one second on every load.
  const [now, setNow] = useState<Date>(() => new Date());

  useEffect(() => {
    let timer = 0;
    let stopped = false;

    /* Re-armed to the next REAL second boundary instead of setInterval(…, 1000). An interval
       measures from whenever the callback actually got to run, so the queueing delay accumulates
       and the display starts skipping seconds (…:01, :03…) after a while. The small +15ms lands
       safely inside the new second — a timer that fires a hair early would otherwise schedule a
       ~1ms follow-up and paint the same second twice. */
    const arm = () => {
      if (stopped) return;
      window.clearTimeout(timer);
      timer = window.setTimeout(() => {
        if (stopped) return;
        setNow(new Date());
        arm();
      }, 1000 - (Date.now() % 1000) + 15);
    };

    /* Background tabs get their timers throttled — heavily, to about once a minute in Chrome
       after a few minutes hidden — and a sleeping laptop stops them altogether. Without this the
       clock would show a stale time for up to a second (or far longer) on the frame you come
       back to. Re-reading on visibility means it is correct on the FIRST paint you see. */
    const resync = () => {
      if (stopped || document.visibilityState !== 'visible') return;
      setNow(new Date());
      arm();
    };

    arm();
    document.addEventListener('visibilitychange', resync);
    return () => {
      stopped = true;
      window.clearTimeout(timer);
      document.removeEventListener('visibilitychange', resync);
    };
  }, []);

  const { date, time } = istNowParts(now);

  /* No aria-live anywhere in here, on purpose: a live region that changes every second makes a
     screen reader announce the time for ever — and there are five of them now. Left silent, it is
     still readable on demand.

     Ordering: Pacific · Mountain | name | Central · Eastern. West → east across the bar, so the
     coasts sit where they do on a map AND the time increases left to right. Eastern is the
     biggest zone at 48.5% and this does put it at the far edge, but a scrambled order costs more
     than the prominence gains.

     No memoisation on the cells. That was the plan while these showed minutes only; with seconds
     every string changes every tick, so a memo would compare and re-render anyway. */
  return (
    <CentreRail>
      <Flank>
        <ZoneClock now={now} zone={US_ZONES_LEFT[0]} />
        <ZoneClock now={now} zone={US_ZONES_LEFT[1]} outer />
      </Flank>

      <Greeting>
        <GreetLine>
          <GreetWord>{greetName ? `${greetWord},` : greetWord}</GreetWord>
          {greetName && <GreetName>{greetName}</GreetName>}
        </GreetLine>
        <ClockLine>
          <span>{date}</span>
          <ClockDot aria-hidden="true">·</ClockDot>
          <ClockTime>{time}</ClockTime>
          <ClockZone>IST</ClockZone>
        </ClockLine>
      </Greeting>

      <Flank>
        <ZoneClock now={now} zone={US_ZONES_RIGHT[0]} outer />
        <ZoneClock now={now} zone={US_ZONES_RIGHT[1]} />
      </Flank>
    </CentreRail>
  );
};

/** [FLUID-8 2026-10-12, user] "the fastness … the same to the complete application": the knob used to start sliding only
 *  after the WHOLE app had re-rendered and re-styled for the new theme (on Inventory / Payments a long moment). It now
 *  slides at the mouse PRESS (touch / keyboard: the click), and the theme swap follows two frames later — the slide is
 *  already running on the compositor and carries on while the page re-colours. One swap at a time. */
const ThemeToggle: React.FC<{ isDark: boolean; onToggle: () => void }> = ({ isDark, onToggle }) => {
  const [knob, setKnob] = useState<boolean | null>(null); // where the knob is going, until the theme has caught up
  useEffect(() => { setKnob(null); }, [isDark]);
  const shown = knob ?? isDark;
  const pressed = useRef(false);
  const frame = useRef(0);
  useEffect(() => () => cancelAnimationFrame(frame.current), []);
  const flip = () => {
    if (knob !== null) return;
    setKnob(!isDark);
    frame.current = requestAnimationFrame(() => { frame.current = requestAnimationFrame(() => { frame.current = 0; onToggle(); }); });
  };
  return (
    <ThemeSwitch
      $isDark={shown}
      aria-label="Toggle theme"
      onPointerDown={(e) => { pressed.current = isPlainMousePress(e); if (pressed.current) flip(); }}
      onClick={(e) => { if (clickEndsPress(pressed, e)) return; flip(); }}
    >
      <SwitchKnob $isDark={shown} />
      <ThemeIcon className="fas fa-sun" $kind="sun" $active={!shown} />
      <ThemeIcon className="fas fa-moon" $kind="moon" $active={shown} />
    </ThemeSwitch>
  );
};

const Header: React.FC<HeaderProps> = ({ onNotifToggle, notifCount = 0, onTrashToggle, trashCount = 0, onAlertsToggle, alertsCount = 0 }) => {
  const { isDark, toggleTheme } = useTheme();
  const { user, logout } = useAuth();
  const isMobile = useIsMobile(); // [MOBILE 2026-07-07] single-button theme toggle on phones
  const navigate = useNavigate(); // [ROW-BUTTON 2026-09-30] the "ROW Dashboard" button
  const location = useLocation(); // [ROW-FULLSCREEN 2026-09-30] so the board's Back returns here
  const [menuOpen, setMenuOpen] = useState(false);
  const [imgError, setImgError] = useState(false);
  const userWrapRef = useRef<HTMLDivElement>(null);
  // [FLUID-8 2026-10-12, user] The profile menu and the App Alerts / Bin / Notifications panels open on the mouse
  // PRESS, like the dropdowns (hooks/usePressAction).
  const menuPress = usePressAction(() => setMenuOpen((o) => !o));
  const alertsPress = usePressAction(() => onAlertsToggle?.());
  const trashPress = usePressAction(() => onTrashToggle?.());
  const notifPress = usePressAction(() => onNotifToggle?.());

  // Close the user menu on outside click or Escape.
  useEffect(() => {
    if (!menuOpen) return;
    const onPointer = (e: MouseEvent) => {
      if (userWrapRef.current && !userWrapRef.current.contains(e.target as Node)) {
        setMenuOpen(false);
      }
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setMenuOpen(false);
    };
    document.addEventListener('mousedown', onPointer);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onPointer);
      document.removeEventListener('keydown', onKey);
    };
  }, [menuOpen]);

  /* [HEADER-GREETING 2026-08-09] Chosen ONCE per page load, never per render. The Header is
     mounted once in AppLayout and survives every tab change, so this stays put while you move
     around the app and re-rolls on the next load. Picking inside the render body would re-roll
     on every parent render and visibly flicker. The hour is likewise read once: text quietly
     rewriting itself as a clock ticks past noon reads as a glitch, not a feature. */
  const greetWord = useMemo(() => {
    const pool = [...greetingsForHour(new Date().getHours()), ...NEUTRAL_GREETINGS];
    return pool[Math.floor(Math.random() * pool.length)];
  }, []);
  const greetName = fullNameFrom(user?.name, user?.email);

  /* [PM-LOGIN 2026-08-11] A username/password session gets a deliberately bare top bar: brand on
     the left, profile (i.e. sign-out) on the right, nothing between. No clocks, no greeting and no
     theme switch — this account exists to look at one page, so every other control is noise. Bin
     and the bell were already gated to admin/senior, so they were never in play here. */
  const isPasswordAccount = !!user?.is_password_account;

  const displayName = user?.name || user?.email || 'User';
  const initial = (displayName.trim()[0] || 'U').toUpperCase();
  const showPhoto = !!user?.picture && !imgError;

  /* [PM-LOGIN 2026-08-11] No bar, no brand, no clocks, no theme switch — just the circle, so the
     one page this account exists to read gets the whole window. Every hook above has already run,
     so this early return is safe. The menu markup is duplicated rather than shared because it is
     the ONLY thing carried over; factoring it out would cost more indirection than it saves. */
  if (isPasswordAccount) {
    return (
      <FloatingProfile>
        <UserWrap ref={userWrapRef}>
          {showPhoto ? (
            <AvatarImg
              src={user!.picture as string}
              alt={displayName}
              onError={() => setImgError(true)}
              {...menuPress}
            />
          ) : (
            <Avatar {...menuPress}>{initial}</Avatar>
          )}

          <Menu $open={menuOpen} role="menu">
            <MenuHead>
              <Avatar as="div">{initial}</Avatar>
              <MenuIdentity>
                <MenuName>{displayName}</MenuName>
                {user?.email && <MenuEmail>{user.email}</MenuEmail>}
              </MenuIdentity>
            </MenuHead>
            <MenuDivider />
            <LogoutItem onClick={() => { setMenuOpen(false); logout(); }}>
              <i className="fas fa-right-from-bracket" />
              Sign out
            </LogoutItem>
          </Menu>
        </UserWrap>
      </FloatingProfile>
    );
  }

  return (
    <HeaderContainer>
      <Left>
        <LogoMark viewBox="0 0 68 68" aria-hidden="true" focusable="false">
          <path
            d="M68 34C68 15.2223 52.7777 0 34 0C15.2223 0 0 15.2223 0 34C0 52.7777 15.2223 68 34 68C52.7777 68 68 52.7777 68 34Z"
            fill="#00D533"
          />
          <path
            d="M52.9856 46.3536L34.0084 31.3495L15.0142 46.3536V34.8903L34.0084 19.8862L52.9856 34.8903V46.3536Z"
            fill="#000000"
          />
        </LogoMark>
        {isMobile ? (
          // [PHONE-LAYOUT 2026-10-24, user] Phones: the whole name — two lines on a narrow bar (PhoneTitle).
          <PhoneTitle><span>The Scale Agency</span><span>Dashboard</span></PhoneTitle>
        ) : (
          <Title>The Scale Agency Dashboard</Title>
        )}
      </Left>
      {/* [HEADER-GREETING 2026-08-09] Rendered only once there is a real user. While auth is
          still resolving `user` is null, and a placeholder greeting would flash and then be
          replaced — worse than the space it fills. */}
      {user && (
        <HeaderCentre greetWord={greetWord} greetName={greetName} />
      )}
      <Actions>
        {/* [ROW-BUTTON 2026-09-30, user] Opens the Team ROW Sales board. First in the row, before App
            Alerts, per the user. Shown to the people the server says may open it — admins, Team ROW - Sales
            and Vishal Suryavanshi since [ROW-ACCESS-TEAM 2026-10-01] (/me can_sales_row, services/salesRowAccess.ts); the /sales-row
            route and /api/sales-row check the same rule, so hiding the button is not the only gate. */}
        {user?.can_sales_row && (
          <ActionItem>
            <ActionSlot>
              <IconButton aria-label="ROW Dashboard" onClick={() => navigate(SALES_ROW.path, { state: { from: location.pathname } })}>
                <i className="fas fa-globe"></i>
              </IconButton>
            </ActionSlot>
            <Caption>ROW Dashboard</Caption>
          </ActionItem>
        )}
        {/* [APP-ALERTS 2026-09-04] Faults the app found in itself — a feed that stopped, money
            counted twice, a website that can't match. Admin-only, gated exactly like the Bin
            below (/api/app-alerts is requireAdmin server-side, so widening this would hand a
            non-admin a button that 403s). Sits LEFT of the Bin, per the user.
            ⚠ A TRIANGLE, not a second bell — the bell beside it already means "unassigned
            refunds and disputes", and two bells would be two things with one meaning. */}
        {user?.is_admin && (
          <ActionItem>
            <ActionSlot>
              <BellWrapper>
                <IconButton aria-label="App Alerts" {...alertsPress}>
                  <i className="fas fa-triangle-exclamation"></i>
                </IconButton>
                {alertsCount > 0 && <NotifBadge>{alertsCount > 99 ? '99+' : alertsCount}</NotifBadge>}
              </BellWrapper>
            </ActionSlot>
            <Caption>App Alerts</Caption>
          </ActionItem>
        )}
        {/* Trash is admin-only — non-admins never see the bin (they also can't
            open the drawer or hit the deleted endpoints, which are requireAdmin). */}
        {user?.is_admin && (
          <ActionItem>
            <ActionSlot>
              <BellWrapper>
                <IconButton aria-label="Deleted (trash)" {...trashPress}>
                  <i className="fas fa-trash-alt"></i>
                </IconButton>
                {trashCount > 0 && <NotifBadge>{trashCount > 99 ? '99+' : trashCount}</NotifBadge>}
              </BellWrapper>
            </ActionSlot>
            <Caption>Bin</Caption>
          </ActionItem>
        )}
        {/* [NOTIF-ADMIN-ONLY 2026-09-16, user] ADMIN ONLY — seniors are excluded too. Assigning
            a case writes the Team + Sales/PM Name onto a refund/dispute row, and the user's
            ruling is that only an admin may do that. Asked whether non-admins should keep a
            read-only bell, they chose to hide it outright, so the whole section goes.
            ⛔ The note that stood here said "UI gate only … no server change is implied". That
            is no longer true and must not be restored: /api/notifications is requireAdmin now,
            so widening this line would hand someone a bell whose every request 403s. */}
        {user?.is_admin && (
          <ActionItem>
            <ActionSlot>
              <BellWrapper>
                <IconButton aria-label="Notifications" {...notifPress}>
                  <i className="fas fa-bell"></i>
                </IconButton>
                {notifCount > 0 && <NotifBadge>{notifCount > 99 ? '99+' : notifCount}</NotifBadge>}
              </BellWrapper>
            </ActionSlot>
            <Caption>Notification</Caption>
          </ActionItem>
        )}
        {/* [MOBILE 2026-07-07] Phones got a single tap-to-toggle icon button here.
            [PHONE-LAYOUT 2026-10-24, user] Phones: no round theme button in the bar any more — the switch is a row of
            the profile menu (ThemeItem, below), so the whole brand name fits. Desktop keeps the sliding sun/moon
            switch — untouched. Same toggleTheme handler either way. */}
        {!isMobile && (
          <ActionItem>
            <ActionSlot>
              <ThemeToggle isDark={isDark} onToggle={toggleTheme} /* [FLUID-8 2026-10-12] the knob moves first */ />
            </ActionSlot>
            <Caption>Theme</Caption>
          </ActionItem>
        )}
        <UserWrap ref={userWrapRef}>
          <ActionSlot>
            {showPhoto ? (
              <AvatarImg
                src={user!.picture as string}
                alt={displayName}
                onError={() => setImgError(true)}
                {...menuPress}
              />
            ) : (
              <Avatar {...menuPress}>{initial}</Avatar>
            )}
          </ActionSlot>
          <Caption>Profile</Caption>

          <Menu $open={menuOpen} role="menu">
            <MenuHead>
              {showPhoto ? (
                <AvatarImg src={user!.picture as string} alt={displayName} onError={() => setImgError(true)} />
              ) : (
                <Avatar as="div">{initial}</Avatar>
              )}
              <MenuIdentity>
                <MenuName>{displayName}</MenuName>
                {user?.email && <MenuEmail>{user.email}</MenuEmail>}
                {user?.role && <RoleBadge $role={user.role}>{ROLE_LABEL[user.role] || user.role}</RoleBadge>}
              </MenuIdentity>
            </MenuHead>
            <MenuDivider />
            {/* [PHONE-LAYOUT 2026-10-24, user] Phones: the theme switch, here instead of a round button in the bar. */}
            {isMobile && (
              <ThemeItem onClick={() => { setMenuOpen(false); toggleTheme(); }}>
                <i className={`fas fa-${isDark ? 'sun' : 'moon'}`} />
                {isDark ? 'Light mode' : 'Dark mode'}
              </ThemeItem>
            )}
            <LogoutItem onClick={() => { setMenuOpen(false); logout(); }}>
              <i className="fas fa-right-from-bracket" />
              Sign out
            </LogoutItem>
          </Menu>
        </UserWrap>
      </Actions>
    </HeaderContainer>
  );
};

export default Header;
